// Live chat streams, held outside the React tree.
//
// A streamed answer used to live in ChatSessionView's local state, with an
// unmount cleanup that aborted the fetch. That made every ordinary navigation
// destructive: the chat tree unmounts when the wizard step changes, when the
// active session changes (it is keyed on session id), and on any route change,
// and each of those threw away the answer mid-sentence.
//
// This module owns the stream instead. It is a plain module singleton rather
// than a React context on purpose:
//   - nothing can unmount it, including a route change (a provider rendered
//     inside RouterProvider would still be torn down by one),
//   - only components that actually subscribe re-render, whereas a provider
//     mounted above the router would re-render the whole app per token.
//
// The remaining loss path is a refresh or tab close, which really does sever
// the connection. Two things cover it: the beforeunload warning registered
// below, and the backend persisting a partial turn as status="interrupted"
// when the client disconnects (see chat_service.send_message_stream).

import { useSyncExternalStore } from "react";
import type { QueryClient } from "@tanstack/react-query";

import { messagesKey, sessionsKey } from "./chatKeys";
import type { AppliedFilters, ChatCitation, ChatMessage, ChatSession } from "./chatApi";
import { chatApi } from "./chatApi";

export interface StreamChatState {
  pending: boolean;
  streamingText: string;
  streamingThinking: string;
  error: string | null;
  // Set when a stream ended without its assistant_message event — the user
  // pressed Stop, or the connection dropped. The partial text stays on screen
  // until the persisted (interrupted) row arrives from a refetch.
  stopped: boolean;
  // Retrieval route chosen by the classifier: "graph" | "grep" | "rag" | "full"
  route: string | null;
}

// Shared frozen snapshot for "no stream for this session". Returning a fresh
// object here would make useSyncExternalStore re-render forever.
const IDLE: StreamChatState = Object.freeze({
  pending: false,
  streamingText: "",
  streamingThinking: "",
  error: null,
  stopped: false,
  route: null,
});

// Deltas arrive far faster than a person can read. Coalescing notifications
// keeps a long answer from re-rendering (and re-parsing markdown) per token.
// A timer, not requestAnimationFrame — rAF is throttled to a crawl in hidden
// tabs, which is exactly where a backgrounded stream runs.
const NOTIFY_THROTTLE_MS = 50;

// How long to wait for the backend's interrupted row before giving up on it.
// That row is written from the server's disconnect teardown, so it can land
// slightly after our abort.
const RECONCILE_DELAYS_MS = [500, 1500];

interface StreamRun {
  controller: AbortController;
  queryClient: QueryClient;
  projectId: string;
  token: number;
}

const entries = new Map<string, StreamChatState>();
const runs = new Map<string, StreamRun>();
const listeners = new Set<() => void>();
let nextToken = 1;
let notifyHandle: ReturnType<typeof setTimeout> | null = null;

// ── Subscription plumbing ────────────────────────────────────────────────────

function flush() {
  if (notifyHandle !== null) {
    clearTimeout(notifyHandle);
    notifyHandle = null;
  }
  for (const listener of listeners) listener();
}

function notify(immediate: boolean) {
  if (immediate) {
    flush();
    return;
  }
  if (notifyHandle !== null) return;
  notifyHandle = setTimeout(flush, NOTIFY_THROTTLE_MS);
}

/** Stable identity — useSyncExternalStore re-subscribes if this changes. */
export function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

export function getStreamState(sessionId: string): StreamChatState {
  return entries.get(sessionId) ?? IDLE;
}

export function isStreaming(sessionId: string): boolean {
  return entries.get(sessionId)?.pending ?? false;
}

export function isAnyStreaming(): boolean {
  for (const entry of entries.values()) if (entry.pending) return true;
  return false;
}

/** True while any session belonging to this project is streaming. */
export function isProjectStreaming(projectId: string): boolean {
  for (const [sessionId, run] of runs) {
    if (run.projectId === projectId && isStreaming(sessionId)) return true;
  }
  return false;
}

function patchEntry(
  sessionId: string,
  patch: Partial<StreamChatState>,
  { immediate = false }: { immediate?: boolean } = {},
) {
  const prev = entries.get(sessionId) ?? IDLE;
  // Always a new object: the snapshot must be immutable for React to see it.
  entries.set(sessionId, { ...prev, ...patch });
  notify(immediate);
}

function clearEntry(sessionId: string) {
  if (!entries.delete(sessionId)) return;
  notify(true);
}

// ── beforeunload ─────────────────────────────────────────────────────────────
//
// The one place a "you will lose this" warning is warranted: a refresh or tab
// close does kill the stream. Registered by the store itself so no component
// has to remember to. Mirrors the handler in useUnsavedChangesGuard.ts.

function warnBeforeUnload(e: BeforeUnloadEvent) {
  e.preventDefault();
  e.returnValue = "";
}

function syncBeforeUnloadListener() {
  if (typeof window === "undefined") return;
  window.removeEventListener("beforeunload", warnBeforeUnload);
  if (isAnyStreaming()) window.addEventListener("beforeunload", warnBeforeUnload);
}

// ── Starting, stopping, reconciling ──────────────────────────────────────────

export interface StartStreamParams {
  sessionId: string;
  projectId: string;
  // Held across unmounts. Safe: the QueryClient is an app-level singleton.
  queryClient: QueryClient;
  content: string;
  documentIds?: string[];
  appliedFilters?: AppliedFilters | null;
  // v2 GraphRAG path — when model is set, the turn is sent directly to the
  // content-intelligence v2/chat endpoint. FRaiME does not persist the messages;
  // synthetic ChatMessage rows are placed in the React Query cache instead.
  model?: string;
  // When true, sends is_advanced_rag=true so the backend routes to /v2/chat.
  isAdvancedRag?: boolean;
}

/**
 * Start a streamed turn. Resolves when the stream ends; rejects if it failed,
 * so the caller can restore the user's draft. Aborting via stopStream is not
 * a failure and does not reject.
 *
 * A second call for a session that is already streaming is ignored — the
 * composer disables Send while pending, and silently replacing a running
 * stream would strand its half-generated answer.
 */
export async function startStream(params: StartStreamParams): Promise<void> {
  const {
    sessionId, projectId, queryClient: qc, content, documentIds, appliedFilters, model, isAdvancedRag,
  } = params;
  if (isStreaming(sessionId)) return;

  const controller = new AbortController();
  const token = nextToken++;
  runs.set(sessionId, { controller, queryClient: qc, projectId, token });
  patchEntry(
    sessionId,
    { pending: true, streamingText: "", streamingThinking: "", error: null, stopped: false, route: null },
    { immediate: true },
  );
  syncBeforeUnloadListener();

  try {
    if (model) {
      // ── v2 GraphRAG path ────────────────────────────────────────────────────
      // Stateless: no FRaiME session/message rows are created. We build the
      // full conversation history from the React Query cache and place
      // synthetic ChatMessage rows back into it after each turn.
      const existing = qc.getQueryData<ChatMessage[]>(messagesKey(sessionId)) ?? [];
      const v2Messages = [
        ...existing
          .filter((m) => m.role === "user" || m.role === "assistant")
          .map((m) => ({ role: m.role as "user" | "assistant", content: m.content })),
        { role: "user" as const, content },
      ];

      // Optimistic user message — shows immediately while the stream starts.
      const userMsg: ChatMessage = {
        id: `v2-user-${Date.now()}`,
        sessionId,
        role: "user",
        content,
        senderUserId: null,
        senderEmail: null,
        citations: null,
        appliedFilters: null,
        thinking: null,
        webCitations: null,
        webSearchStatus: null,
        status: null,
        createdAt: new Date().toISOString(),
      };
      qc.setQueryData<ChatMessage[]>(messagesKey(sessionId), (prev) => [
        ...(prev ?? []),
        userMsg,
      ]);

      const collectedCitations: ChatCitation[] = [];
    } else {
      // ── v1 FRaiME path ──────────────────────────────────────────────────────
      await chatApi.streamMessage(
        sessionId,
        content,
        {
          onUserMessage: (message) => {
            qc.setQueryData<ChatMessage[]>(messagesKey(sessionId), (prev) => [
              ...(prev ?? []),
              message,
            ]);
          },
          onSessionUpdate: ({ id, title }) => {
            qc.setQueryData<ChatSession[]>(sessionsKey(projectId), (prev) =>
              (prev ?? []).map((s) => (s.id === id ? { ...s, title } : s)),
            );
          },
          onThinking: (text) => {
            const prev = getStreamState(sessionId);
            patchEntry(sessionId, { streamingThinking: prev.streamingThinking + text });
          },
          onDelta: (text) => {
            const prev = getStreamState(sessionId);
            patchEntry(sessionId, { streamingText: prev.streamingText + text });
          },
          onAssistantMessage: (message) => {
            // Capture route before clearEntry wipes the stream state.
            const { route } = getStreamState(sessionId);
            qc.setQueryData<ChatMessage[]>(messagesKey(sessionId), (prev) => [
              ...(prev ?? []),
              { ...message, route },
            ]);
            // The persisted row now carries the text, so drop the live copy
            // entirely rather than leaving a spent entry behind.
            clearEntry(sessionId);
            qc.invalidateQueries({ queryKey: sessionsKey(projectId) });
          },
          onError: (detail) => {
            patchEntry(
              sessionId,
              { pending: false, streamingText: "", streamingThinking: "", error: detail },
              { immediate: true },
            );
          },
          onDone: () => {
            // Reached with pending still true only when the stream ended without
            // a terminal event — a dropped connection. Treat it like a stop and
            // let the reconcile pick up the interrupted row.
            if (!isStreaming(sessionId)) return;
            patchEntry(sessionId, { pending: false, stopped: true }, { immediate: true });
            void reconcileInterrupted(sessionId, token);
          },
          onStatus: ({ phase, route }) => {
            if (phase === "routing" && route) {
              patchEntry(sessionId, { route }, { immediate: true });
            }
          },
        },
        { documentIds, appliedFilters, signal: controller.signal, isAdvancedRag },
      );
    }
  } catch (err) {
    if (controller.signal.aborted) return; // stopStream already set the state
    const message = err instanceof Error ? err.message : "Failed to send message";
    patchEntry(
      sessionId,
      { pending: false, streamingText: "", streamingThinking: "", error: message },
      { immediate: true },
    );
    throw err;
  } finally {
    if (runs.get(sessionId)?.token === token) runs.delete(sessionId);
    syncBeforeUnloadListener();
  }
}

/**
 * Abort a stream at the user's request. Keeps the partial text on screen and
 * refetches, because the backend persists what it generated as an interrupted
 * turn when we disconnect — that persisted row is what should end up in the
 * thread, not our local copy.
 */
export function stopStream(sessionId: string): void {
  const run = runs.get(sessionId);
  if (!run) {
    // Nothing in flight — clear any leftover text so the view settles.
    clearEntry(sessionId);
    return;
  }
  run.controller.abort();
  patchEntry(sessionId, { pending: false, stopped: true }, { immediate: true });
  syncBeforeUnloadListener();
  void reconcileInterrupted(sessionId, run.token);
}

async function reconcileInterrupted(sessionId: string, token: number) {
  const run = runs.get(sessionId);
  const qc = run?.queryClient;
  const projectId = run?.projectId;
  if (!qc) {
    clearEntry(sessionId);
    return;
  }
  try {
    for (const delay of RECONCILE_DELAYS_MS) {
      await new Promise((resolve) => setTimeout(resolve, delay));
      // A new turn started in the meantime — its state owns this session now.
      if (runs.get(sessionId)?.token !== token && isStreaming(sessionId)) return;
      await qc.refetchQueries({ queryKey: messagesKey(sessionId) });
      const messages = qc.getQueryData<ChatMessage[]>(messagesKey(sessionId));
      if (messages?.[messages.length - 1]?.role === "assistant") break;
    }
    if (projectId) qc.invalidateQueries({ queryKey: sessionsKey(projectId) });
  } catch {
    // Offline, or the session is gone; fall through and clear the local copy.
  } finally {
    if (!isStreaming(sessionId)) clearEntry(sessionId);
  }
}

/** Abort everything — used on sign-out, when the token is being torn down. */
export function abortAllStreams(): void {
  for (const run of runs.values()) run.controller.abort();
  runs.clear();
  entries.clear();
  syncBeforeUnloadListener();
  notify(true);
}

// ── Hooks ────────────────────────────────────────────────────────────────────

/** Full live state for a session's stream. Re-renders as text arrives. */
export function useChatStream(sessionId: string): StreamChatState {
  return useSyncExternalStore(
    subscribe,
    () => getStreamState(sessionId),
    () => IDLE,
  );
}

/**
 * Whether this session is streaming. Returns a boolean rather than the state
 * object so indicators do not re-render on every delta.
 */
export function useIsStreaming(sessionId: string): boolean {
  return useSyncExternalStore(
    subscribe,
    () => isStreaming(sessionId),
    () => false,
  );
}

/**
 * Whether this project has a stream running — for "still generating" hints
 * shown outside the chat view, where the streaming session may not be visible.
 */
export function useIsProjectStreaming(projectId: string): boolean {
  return useSyncExternalStore(
    subscribe,
    () => isProjectStreaming(projectId),
    () => false,
  );
}
